import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Calculator, PoundSterling, TrendingDown, Home, ChevronDown, ChevronUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ============ MORTGAGE AFFORDABILITY CALCULATOR ============

export const MortgageCalculator = ({ propertyPrice = 0, country = 'UK', currencySymbol = '£' }) => {
  const [config, setConfig] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [deposit, setDeposit] = useState(10);
  const [interestRate, setInterestRate] = useState(5.25);
  const [termYears, setTermYears] = useState(25);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/calculators/config`);
      const mc = res.data?.mortgage_calculator;
      if (mc) {
        setConfig(mc);
        const countryConfig = mc[country] || mc['UK'];
        setInterestRate(countryConfig.default_interest_rate);
        setTermYears(countryConfig.default_term_years);
        setDeposit(countryConfig.default_deposit_percent);
      }
    } catch (e) { /* use defaults */ }
  }, [country]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const isEnabled = config ? config.enabled !== false : true;

  const calculation = useMemo(() => {
    const loanAmount = propertyPrice * (1 - deposit / 100);
    const monthlyRate = interestRate / 100 / 12;
    const numPayments = termYears * 12;

    if (monthlyRate === 0) {
      return {
        monthlyPayment: loanAmount / numPayments,
        totalPayment: loanAmount,
        totalInterest: 0,
        loanAmount,
        depositAmount: propertyPrice * (deposit / 100),
      };
    }

    const monthlyPayment = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1);
    const totalPayment = monthlyPayment * numPayments;
    const totalInterest = totalPayment - loanAmount;

    return {
      monthlyPayment,
      totalPayment,
      totalInterest,
      loanAmount,
      depositAmount: propertyPrice * (deposit / 100),
    };
  }, [propertyPrice, deposit, interestRate, termYears]);

  if (!isEnabled || !propertyPrice) return null;

  const countryConfig = config?.[country] || config?.['UK'] || {};
  const fmt = (n) => `${currencySymbol}${Math.round(n).toLocaleString('en-GB')}`;

  return (
    <div className="luxury-card border border-slate-200 overflow-hidden" data-testid="mortgage-calculator">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-5 hover:bg-slate-50/50 transition-colors"
        data-testid="mortgage-calculator-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-blue-50 flex items-center justify-center">
            <Calculator className="h-4 w-4 text-blue-600" />
          </div>
          <div className="text-left">
            <p className="text-[#1A1A1A] font-semibold text-sm">Mortgage Calculator</p>
            <p className="text-[#1A1A1A]/40 text-xs font-medium">{fmt(calculation.monthlyPayment)}/mo estimated</p>
          </div>
        </div>
        {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>

      {expanded && (
        <div className="px-5 pb-5 border-t border-slate-100 pt-4">
          {/* Sliders */}
          <div className="space-y-5">
            <div>
              <div className="flex justify-between text-xs mb-2">
                <span className="text-slate-500 font-medium">Deposit</span>
                <span className="text-[#1A1A1A] font-semibold">{deposit}% ({fmt(calculation.depositAmount)})</span>
              </div>
              <input
                type="range"
                min={5} max={50} step={1}
                value={deposit}
                onChange={e => setDeposit(Number(e.target.value))}
                className="w-full accent-[#E07A3A] h-1.5"
                data-testid="mortgage-deposit-slider"
              />
            </div>

            <div>
              <div className="flex justify-between text-xs mb-2">
                <span className="text-slate-500 font-medium">Interest Rate</span>
                <span className="text-[#1A1A1A] font-semibold">{interestRate.toFixed(2)}%</span>
              </div>
              <input
                type="range"
                min={countryConfig.min_interest_rate || 1}
                max={countryConfig.max_interest_rate || 15}
                step={0.25}
                value={interestRate}
                onChange={e => setInterestRate(Number(e.target.value))}
                className="w-full accent-[#E07A3A] h-1.5"
                data-testid="mortgage-rate-slider"
              />
            </div>

            <div>
              <div className="flex justify-between text-xs mb-2">
                <span className="text-slate-500 font-medium">Term</span>
                <span className="text-[#1A1A1A] font-semibold">{termYears} years</span>
              </div>
              <input
                type="range"
                min={countryConfig.min_term_years || 5}
                max={countryConfig.max_term_years || 40}
                step={1}
                value={termYears}
                onChange={e => setTermYears(Number(e.target.value))}
                className="w-full accent-[#E07A3A] h-1.5"
                data-testid="mortgage-term-slider"
              />
            </div>
          </div>

          {/* Results */}
          <div className="mt-5 bg-[#0F172A] p-4 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-white/50 text-xs font-medium">Monthly Payment</span>
              <span className="text-white font-['Playfair_Display'] text-xl font-semibold" data-testid="mortgage-monthly-result">{fmt(calculation.monthlyPayment)}</span>
            </div>
            <div className="border-t border-white/10 pt-3 space-y-1.5">
              <div className="flex justify-between text-xs">
                <span className="text-white/40 font-medium">Loan Amount</span>
                <span className="text-white/70 font-medium">{fmt(calculation.loanAmount)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-white/40 font-medium">Total Interest</span>
                <span className="text-white/70 font-medium">{fmt(calculation.totalInterest)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-white/40 font-medium">Total Repayment</span>
                <span className="text-white/70 font-medium">{fmt(calculation.totalPayment)}</span>
              </div>
            </div>
          </div>

          <p className="text-xs text-slate-400 mt-3 leading-relaxed font-medium">
            Indicative only. Consult a qualified mortgage adviser. Shopperden does not provide financial advice.
          </p>
        </div>
      )}
    </div>
  );
};


// ============ STAMP DUTY CALCULATOR ============

export const StampDutyCalculator = ({ propertyPrice = 0, country = 'UK', currencySymbol = '£' }) => {
  const [config, setConfig] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [firstTimeBuyer, setFirstTimeBuyer] = useState(false);
  const [additionalProperty, setAdditionalProperty] = useState(false);
  const [selectedState, setSelectedState] = useState('Maharashtra');
  const [buyerGender, setBuyerGender] = useState('male');
  const [selectedProvince, setSelectedProvince] = useState('Ontario');

  const fetchConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/calculators/config`);
      const sc = res.data?.stamp_duty_calculator;
      if (sc) setConfig(sc);
    } catch (e) { /* use defaults */ }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const isEnabled = config ? config.enabled !== false : true;

  const calculation = useMemo(() => {
    if (!config || !propertyPrice) return null;

    if (country === 'UK') {
      const ukConfig = config.UK;
      if (!ukConfig) return null;

      let bands = ukConfig.bands;
      if (firstTimeBuyer && ukConfig.first_time_buyer_relief && propertyPrice <= 625000) {
        bands = ukConfig.first_time_bands;
      }

      let duty = 0;
      for (const band of bands) {
        const upper = band.to === -1 ? Infinity : band.to;
        if (propertyPrice > band.from) {
          const taxable = Math.min(propertyPrice, upper) - band.from;
          duty += taxable * (band.rate / 100);
        }
      }

      if (additionalProperty && ukConfig.additional_property_surcharge) {
        duty += propertyPrice * (ukConfig.additional_property_surcharge / 100);
      }

      const effectiveRate = propertyPrice > 0 ? (duty / propertyPrice) * 100 : 0;
      return { duty: Math.round(duty), effectiveRate: effectiveRate.toFixed(2), note: ukConfig.note };
    }

    if (country === 'IN') {
      const inConfig = config.IN;
      if (!inConfig) return null;

      const stateData = inConfig.states.find(s => s.name === selectedState);
      if (!stateData) return null;

      const dutyRate = buyerGender === 'female' ? stateData.stamp_duty_female : stateData.stamp_duty_male;
      const regRate = stateData.registration;
      const stampDuty = propertyPrice * (dutyRate / 100);
      const registration = propertyPrice * (regRate / 100);
      const total = stampDuty + registration;
      const effectiveRate = propertyPrice > 0 ? (total / propertyPrice) * 100 : 0;

      return {
        duty: Math.round(total),
        stampDuty: Math.round(stampDuty),
        registration: Math.round(registration),
        dutyRate,
        regRate,
        effectiveRate: effectiveRate.toFixed(2),
        note: inConfig.note
      };
    }

    if (country === 'CA') {
      const caConfig = config.CA;
      if (!caConfig) return null;

      const provData = caConfig.provinces?.find(p => p.name === selectedProvince);
      if (!provData || !provData.bands || provData.bands.length === 0) {
        return { duty: 0, effectiveRate: '0.00', note: `${selectedProvince} does not charge Land Transfer Tax.`, noTax: true };
      }

      let tax = 0;
      for (const band of provData.bands) {
        const upper = band.to === -1 ? Infinity : band.to;
        if (propertyPrice > band.from) {
          const taxable = Math.min(propertyPrice, upper) - band.from;
          tax += taxable * (band.rate / 100);
        }
      }

      if (firstTimeBuyer && provData.first_time_rebate) {
        tax = Math.max(0, tax - provData.first_time_rebate);
      }

      const effectiveRate = propertyPrice > 0 ? (tax / propertyPrice) * 100 : 0;
      return {
        duty: Math.round(tax),
        effectiveRate: effectiveRate.toFixed(2),
        rebate: firstTimeBuyer ? provData.first_time_rebate : 0,
        note: caConfig.note
      };
    }

    return null;
  }, [config, propertyPrice, country, firstTimeBuyer, additionalProperty, selectedState, buyerGender, selectedProvince]);

  if (!isEnabled || !propertyPrice) return null;

  const fmt = (n) => `${currencySymbol}${Math.round(n).toLocaleString('en-GB')}`;
  const label = config?.[country]?.label || 'Stamp Duty';

  return (
    <div className="luxury-card border border-slate-200 overflow-hidden" data-testid="stamp-duty-calculator">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-5 hover:bg-slate-50/50 transition-colors"
        data-testid="stamp-duty-calculator-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-emerald-50 flex items-center justify-center">
            <Home className="h-4 w-4 text-emerald-600" />
          </div>
          <div className="text-left">
            <p className="text-[#1A1A1A] font-semibold text-sm">{label} Calculator</p>
            <p className="text-[#1A1A1A]/40 text-xs font-medium">{calculation ? `${fmt(calculation.duty)} estimated` : 'Calculate your tax'}</p>
          </div>
        </div>
        {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>

      {expanded && (
        <div className="px-5 pb-5 border-t border-slate-100 pt-4">
          {country === 'UK' && (
            <div className="space-y-3 mb-5">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={firstTimeBuyer}
                  onChange={e => setFirstTimeBuyer(e.target.checked)}
                  className="accent-[#E07A3A] w-4 h-4"
                  data-testid="stamp-duty-ftb-checkbox"
                />
                <span className="text-sm text-[#1A1A1A] font-medium">First-time buyer</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={additionalProperty}
                  onChange={e => setAdditionalProperty(e.target.checked)}
                  className="accent-[#E07A3A] w-4 h-4"
                  data-testid="stamp-duty-additional-checkbox"
                />
                <span className="text-sm text-[#1A1A1A] font-medium">Additional property (+3% surcharge)</span>
              </label>
            </div>
          )}

          {country === 'IN' && config?.IN?.states && (
            <div className="space-y-3 mb-5">
              <div>
                <label className="text-xs text-slate-500 font-medium uppercase tracking-wider block mb-1">State</label>
                <select
                  value={selectedState}
                  onChange={e => setSelectedState(e.target.value)}
                  className="w-full border border-slate-200 px-3 py-2.5 text-sm text-[#1A1A1A] focus:border-[#E07A3A] focus:outline-none bg-white"
                  data-testid="stamp-duty-state-select"
                >
                  {config.IN.states.map(s => (
                    <option key={s.name} value={s.name}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-500 font-medium uppercase tracking-wider block mb-1">Buyer Gender</label>
                <div className="flex gap-3">
                  {['male', 'female'].map(g => (
                    <button
                      key={g}
                      onClick={() => setBuyerGender(g)}
                      className={`flex-1 py-2 text-sm font-medium border transition-colors ${buyerGender === g ? 'border-[#E07A3A] bg-[#FEF3EC] text-[#E07A3A]' : 'border-slate-200 text-slate-500'}`}
                      data-testid={`stamp-duty-gender-${g}`}
                    >
                      {g.charAt(0).toUpperCase() + g.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {country === 'CA' && config?.CA?.provinces && (
            <div className="space-y-3 mb-5">
              <div>
                <label className="text-xs text-slate-500 font-medium uppercase tracking-wider block mb-1">Province</label>
                <select
                  value={selectedProvince}
                  onChange={e => setSelectedProvince(e.target.value)}
                  className="w-full border border-slate-200 px-3 py-2.5 text-sm text-[#1A1A1A] focus:border-[#E07A3A] focus:outline-none bg-white"
                  data-testid="ltt-province-select"
                >
                  {config.CA.provinces.map(p => (
                    <option key={p.name} value={p.name}>{p.name}</option>
                  ))}
                </select>
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={firstTimeBuyer}
                  onChange={e => setFirstTimeBuyer(e.target.checked)}
                  className="accent-[#E07A3A] w-4 h-4"
                  data-testid="ltt-ftb-checkbox"
                />
                <span className="text-sm text-[#1A1A1A] font-medium">First-time home buyer</span>
              </label>
            </div>
          )}

          {/* Results */}
          {calculation && (
            <div className="bg-[#0F172A] p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-white/50 text-xs font-medium">Total {label}</span>
                <span className="text-white font-['Playfair_Display'] text-xl font-semibold" data-testid="stamp-duty-result">{fmt(calculation.duty)}</span>
              </div>
              <div className="border-t border-white/10 pt-3 space-y-1.5">
                {country === 'IN' && calculation.stampDuty !== undefined && (
                  <>
                    <div className="flex justify-between text-xs">
                      <span className="text-white/40 font-medium">Stamp Duty ({calculation.dutyRate}%)</span>
                      <span className="text-white/70 font-medium">{fmt(calculation.stampDuty)}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-white/40 font-medium">Registration ({calculation.regRate}%)</span>
                      <span className="text-white/70 font-medium">{fmt(calculation.registration)}</span>
                    </div>
                  </>
                )}
                {country === 'CA' && calculation.rebate > 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-white/40 font-medium">First-time Rebate Applied</span>
                    <span className="text-emerald-400 font-medium">-{fmt(calculation.rebate)}</span>
                  </div>
                )}
                {country === 'CA' && calculation.noTax && (
                  <div className="text-xs text-white/50 font-medium text-center py-1">{calculation.note}</div>
                )}
                <div className="flex justify-between text-xs">
                  <span className="text-white/40 font-medium">Effective Rate</span>
                  <span className="text-white/70 font-medium">{calculation.effectiveRate}%</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-white/40 font-medium">Property Price</span>
                  <span className="text-white/70 font-medium">{fmt(propertyPrice)}</span>
                </div>
              </div>
            </div>
          )}

          {calculation?.note && (
            <p className="text-xs text-slate-400 mt-3 leading-relaxed font-medium">{calculation.note}</p>
          )}
          <p className="text-xs text-slate-400 mt-1 font-medium">
            Indicative only. Consult a solicitor for exact amounts.
          </p>
        </div>
      )}
    </div>
  );
};
